import { registerSW } from 'virtual:pwa-register';
import { clearAllUserCaches } from './caches';
import { createStore } from './store';
import { fetchServedVersion } from './version';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface PwaState {
  /** The browser offered an install prompt (`beforeinstallprompt`) that has not been used. */
  canInstall: boolean;
  /** A new service worker is waiting; `applyUpdate()` reloads into it. */
  updateReady: boolean;
  /** The version the waiting update installs (from /version.json), when known. */
  availableVersion: string | null;
  /** The app shell is cached for offline use. */
  offlineReady: boolean;
  /**
   * Plume was installed during this page session (`appinstalled`, or the
   * install prompt was accepted). The tab itself still runs in the browser;
   * `platform.isStandalone` tells whether this page is the installed app.
   */
  installed: boolean;
}

export const pwaStore = createStore<PwaState>({
  canInstall: false,
  updateReady: false,
  availableVersion: null,
  offlineReady: false,
  installed: false,
});

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let updateSW: ((reload?: boolean) => Promise<void>) | null = null;

let navigateHandler: (url: string) => void = () => undefined;
let initialised = false;

export function initPwa(onNavigate: (url: string) => void): void {
  navigateHandler = onNavigate;
  if (initialised) return;
  initialised = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    pwaStore.set((s) => ({ ...s, canInstall: true }));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    pwaStore.set((s) => ({ ...s, canInstall: false, installed: true }));
  });

  if ('serviceWorker' in navigator) {
    updateSW = registerSW({
      immediate: true,
      onNeedRefresh: () => {
        pwaStore.set((s) => ({ ...s, updateReady: true }));
        void fetchServedVersion().then((availableVersion) => pwaStore.set((s) => ({ ...s, availableVersion })));
      },
      onOfflineReady: () => pwaStore.set((s) => ({ ...s, offlineReady: true })),
      onRegisteredSW: (_url, reg) => {
        // Check for app updates hourly while open (long listening sessions).
        if (reg) setInterval(() => void reg.update().catch(() => undefined), 60 * 60 * 1000);
      },
    });
    navigator.serviceWorker.addEventListener('message', (e: MessageEvent<{ type?: string; url?: string }>) => {
      if (e.data?.type === 'NAVIGATE' && e.data.url) navigateHandler(e.data.url);
    });
  }
}

export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) return false;
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  const accepted = outcome === 'accepted';
  pwaStore.set((s) => ({ ...s, canInstall: false, installed: s.installed || accepted }));
  return accepted;
}

export async function applyUpdate(): Promise<void> {
  await updateSW?.(true);
}

/**
 * Drops cached library data and covers (on sign-out or session expiry) so the
 * next account never sees the previous one's library. Cache Storage is shared
 * with the service worker, so the page can do this even when it isn't controlled.
 */
export function clearUserCaches(): Promise<void> {
  return clearAllUserCaches();
}
